import { AfterViewInit, Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';
import { IContact, Salutations } from '../models/contact';
import { ContactService } from '../services/contact.service';

import {Location} from '@angular/common'; 
import { translate } from '@jsverse/transloco';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { ListQueryStateService } from 'src/app/shared/services/list-query-state.service';

@UntilDestroy()
@Component({
    selector: 'app-contact',
    templateUrl: './contact.component.html',
    styleUrls: ['./contact.component.scss'],
    standalone: false
})
export class ContactComponent implements OnInit {

  constructor(
    private contactService: ContactService,
    private activatedRoute : ActivatedRoute,
    private router : Router,
    private location: Location,
    private appNotificationService: AppNotificationService,
    private permissionStore: PermissionStorageService,
    private listQueryState: ListQueryStateService
  ) { }

  contact: IContact;
  canEdit = false;

  public deletionDialogOpened = false;

  form: UntypedFormGroup;

  // TODO: Enum to Array for Salutations
  // salutaions = enumToArray(Salutations, "salutations.");
  salutations = enumToArray(Salutations, "contact.salutations.");

  ngOnInit(): void {
    this.contact = this.activatedRoute.snapshot.data['contact'] as IContact;
    this.canEdit = this.hasPermission('Permissions.Contacts.Update');

    this.initFrom(this.contact ? this.contact : null);

    this.listenToCompanyFieldChanges();
    if(this.contact){
      this.setFormValidators(this.contact.isCompany)
    }

    if (!this.canEdit) {
      this.form.disable();
    }
  }

  private hasPermission(permission: string): boolean {
    const permissions = this.permissionStore.getPermissions();
    return !!permissions?.find(p => p === permission);
  }

  defaultSalutation = {
    id: null,
    name: translate('contact.chooseSalutation')
  }

  onChanges(){
    
  }
  
  initFrom(contact: IContact | null){
    this.form = new UntypedFormGroup({
      id: new UntypedFormControl(contact?.id ?? null),
      externalNumber: new UntypedFormControl(contact?.externalNumber ?? null),
      companyName: new UntypedFormControl({value: contact?.companyName ?? null, disabled : !contact?.isCompany}),
      firstName: new UntypedFormControl({value: contact?.firstName ?? null, disabled : contact?.isCompany}, Validators.required),
      lastName: new UntypedFormControl({value: contact?.lastName ?? null, disabled : contact?.isCompany}, Validators.required),
      title: new UntypedFormControl({value: contact?.title ?? null, disabled : contact?.isCompany}),
      salutation: new UntypedFormControl({value: contact?.salutation ?? null, disabled : contact?.isCompany}, Validators.required),
      isCompany: new UntypedFormControl(contact?.isCompany ?? false),
      street: new UntypedFormControl(contact?.street ?? null),
      city: new UntypedFormControl(contact?.city ?? null),
      zip: new UntypedFormControl(contact?.zip ?? null, {validators: Validators.pattern('[0-9]{4,5}'), updateOn: 'blur'}),
      country: new UntypedFormControl(contact?.country ?? null),
      phone: new UntypedFormControl(contact?.phone ?? null, {validators: Validators.pattern('[0-9\+]{10,12}'), updateOn: 'blur'}),
      email: new UntypedFormControl(contact?.email ?? null, {validators: Validators.email, updateOn: 'blur'}),
      website: new UntypedFormControl(contact?.website ?? null, {validators: Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?'), updateOn: 'blur'}),
      remarks: new UntypedFormControl(contact?.remarks ?? null),
      contactPerson: new UntypedFormControl({value: contact?.contactPerson ?? null, disabled: !contact?.isCompany }),
    })
  }

  updateForm(contact: IContact){
    this.form.patchValue({
      id: contact.id ?? null,
      externalNumber: contact.externalNumber ?? null,
      companyName:contact.companyName ?? null,
      firstName: contact.firstName ?? null,
      lastName: contact.lastName ?? null,
      title: contact.title ?? null,
      salutation: contact.salutation ?? null,
      isCompany: contact.isCompany ?? null,
      street: contact.street ?? null,
      city: contact.city ?? null,
      zip: contact.zip ?? null,
      country: contact.country ?? null,
      phone: contact.phone ?? null,
      email: contact.email ?? null,
      website: contact.website ?? null,
      remarks: contact.remarks ?? null,
      contactPerson: contact.contactPerson ?? null
    })
  }

  onSubmit(){
    const formValues = this.form.value;

    if(formValues.id){
      this.contactService.updateContact(formValues)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.contactService.getContact(response)
        .pipe(untilDestroyed(this))
        .subscribe(response => {
          this.contact = response;
          this.updateForm(this.contact);
          this.appNotificationService.notification$.next({message: translate('contact.saveSuccess'), style: 'success'})

        })
      })
    } else {
      this.contactService.addContact(formValues)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.contactService.getContact(response)
        .pipe(untilDestroyed(this))
        .subscribe(response => {
          this.contact = response;
          this.updateForm(this.contact);
          this.form.markAsPristine();
          this.location.replaceState(`/contact/${response.id}`);
          this.appNotificationService.notification$.next({message: translate('contact.createSuccess'), style: 'success'})
        })
      })
    }
  }

  listenToCompanyFieldChanges(){
    this.form.get('isCompany').valueChanges
    .pipe(untilDestroyed(this))
    .subscribe((event) => {
      this.setFormValidators(event);
    })
  }

  call(phone: string) {
    if (!phone) return;
  
    // Sanitize number (optional)
    const cleaned = phone.replace(/\s+/g, '');
  
    window.location.href = `tel:${cleaned}`;
  }
  

  setFormValidators(isCompany){

    // TODO: Optimize so it only activates if the isCompany Field has changed
    // this.form.valueChanges

    if(isCompany){
      this.form.get('firstName').disable();
      this.form.get('lastName').disable();
      this.form.get('salutation').disable();
      this.form.get('title').disable();
      this.form.get('companyName').setValidators(Validators.required);
      if (this.canEdit) {
        this.form.get('companyName').enable();
        this.form.get('contactPerson').enable();
      }
    } else {
      this.form.get('firstName').setValidators(Validators.required);
      this.form.get('lastName').setValidators(Validators.required);
      this.form.get('salutation').setValidators(Validators.required);
      if (this.canEdit) {
        this.form.get('firstName').enable();
        this.form.get('lastName').enable();
        this.form.get('salutation').enable();
        this.form.get('title').enable();
      }
      this.form.get('companyName').disable();
      this.form.get('contactPerson').disable();
    }
  };

  /** Back to the list with the paging/keyword the user left it with. */
  goBack(){
    this.router.navigate(['/contacts'], {
      queryParams: this.listQueryState.getQueryParams('contacts')
    });
  }

  delete(){
    this.contactService.deleteContact(this.contact.id)
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.goBack();
      this.appNotificationService.notification$.next({message: translate('contact.deleteSuccess'), style: 'success'})
    })
  }

  // delete confirmation dialog
  public deletionDialogOpen(): void {
    this.deletionDialogOpened = true;
  }

}
